import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import { NewRelicIntegrationFormUpdateIntegrationMutation } from "./__generated__/NewRelicIntegrationFormUpdateIntegrationMutation.graphql";
import { NewRelicIntegrationFormValidateIntegrationMutation } from "./__generated__/NewRelicIntegrationFormValidateIntegrationMutation.graphql";

// Define the mutations
const updateIntegrationMutation = graphql`
  mutation NewRelicIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation NewRelicIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface NewRelicIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
}

export default function NewRelicIntegrationForm({
  onComplete,
  onCancel
}: NewRelicIntegrationFormProps) {
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  // Set up the mutations
  const [commitMutation, isMutationInFlight] = useMutation<NewRelicIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<NewRelicIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  // Handle testing the integration
  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    // Validate required fields
    if (!accountId.trim()) {
      setFormError("Account ID is required");
      return;
    }
    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }
    if (!region.trim()) {
      setFormError("Region is required");
      return;
    }

    // Build credentials object
    const credentials = {
      account_id: accountId.trim(),
      api_key: apiKey.trim(),
      region: region.trim()
    };

    // Prepare the input for the validation mutation
    const input = {
      integrationType: "NEW_RELIC" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    // Execute the validation mutation
    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          return;
        }

        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }

        // Success!
        setValidationSuccess("✓ Credentials validated successfully!");
        setIsValidated(true);
      },
      onError: error => {
        setFormError(error.message);
      }
    });
  };

  // Handle saving the integration
  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    // Validate required fields
    if (!accountId.trim()) {
      setFormError("Account ID is required");
      return;
    }
    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }
    if (!region.trim()) {
      setFormError("Region is required");
      return;
    }

    // Build credentials object
    const credentials = {
      account_id: accountId.trim(),
      api_key: apiKey.trim(),
      region: region.trim()
    };

    // Prepare the input for the mutation
    const input = {
      integrationType: "NEW_RELIC" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };

    // Execute the mutation
    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        // Success!
        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">API Key Configuration</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account-id" className="text-muted-foreground">Account ID</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="account-id"
                    type="text"
                    value={accountId}
                    onChange={(e) => {
                      setAccountId(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="Enter your New Relic Account ID"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-key" className="text-muted-foreground">API Key</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="api-key"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="Enter your New Relic API Key"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="region" className="text-muted-foreground">Region</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <Input
                    id="region"
                    type="text"
                    value={region}
                    onChange={(e) => {
                      setRegion(e.target.value);
                      setIsValidated(false);
                      setValidationSuccess(null);
                    }}
                    placeholder="Enter your region (US or EU)"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
